import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, NavigationStart } from '@angular/router';
import { SearchResult, SearchService } from 'src/app/services/search.service';
import { selectSearchId } from 'src/app/store/selectors/files.selector';
import { searchClick } from 'src/app/store/actions/files.actions';
import { debounce, distinctUntilChanged, filter, switchMap, take, catchError } from 'rxjs/operators';
import { take as takeOperator } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { clearSearch, search } from 'src/app/store/actions/files.actions';
import { of, timer, forkJoin, Observable } from 'rxjs';
import { selectSearchIsLoading, selectSearchQuery, selectSearchResults } from 'src/app/store/selectors/files.selector';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { selectDomain } from 'src/app/store/selectors/auth.selectors';
import { Domain } from 'src/app/services/domain.service';
import { ArticleSearchService, ArticleSearchResult } from 'src/app/services/article-search.service';
import { searchResultAnimations } from './search-files.animations';
import { Router as AngularRouter } from '@angular/router';
import { SearchStateService } from 'src/app/services/search-state.service';

@Component({
  selector: 'mvp-search-files',
  templateUrl: './search-files.component.html',
  animations: searchResultAnimations,
})
export class SearchFilesComponent implements OnInit {
  showSuggestionBox: boolean = false;
  searchId$ = this.store.select(selectSearchId);
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('articlesList', { static: false }) articlesList: ElementRef<HTMLUListElement>;

  contactForm: FormGroup;
  contactRequestStatus: 'success' | 'error' | null = null;
  contactResponseMessage = '';
  domain$: Observable<Domain>;

  form: FormGroup = this.formBuilder.group({ input: ['', [Validators.required, Validators.minLength(3)]] });
  isLoading$: Observable<boolean>;
  searchResults$: Observable<SearchResult[]>;
  searchValue$: Observable<string>;

  notionSuggestions: string[] = [];
  currentQuery = '';
  currentResults: SearchResult[] = [];
  articleResults: ArticleSearchResult[] = [];
  combinedResults: SearchResult[] = [];
  
  activeArticleIndex: number = 0;
  assetUrl: string = environment.assetUrl;
  environment = environment;

  private lastActive = false;
  searchLaunched = false;
  showArticles = false;
  showRemainingFaqs = false;

  @Output() searchActiveChange = new EventEmitter<boolean>();

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private store: Store,
    private searchService: SearchService,
    private elementRef: ElementRef,
    private articleSearchService: ArticleSearchService,
    private angularRouter: AngularRouter,
    private searchStateService: SearchStateService
  ) {
    this.domain$ = this.store.select(selectDomain);
    this.searchResults$ = this.store.select(selectSearchResults);
    this.isLoading$ = this.store.select(selectSearchIsLoading);
    this.searchValue$ = this.store.select(selectSearchQuery);
    this.contactForm = new FormGroup({ message: new FormControl('', Validators.required) });
  }

  ngOnInit() {
    this.form.get('input').valueChanges.subscribe(value => {
      this.showSuggestionBox = (value?.length || 0) > 0;
      this.searchStateService.setSearchValue(value);
    });
    const specials = ["aide", "jour", "nuit", "paie"];
    this.form.get('input').valueChanges.pipe(
      distinctUntilChanged(),
      filter((value: string) => value.length === 0 && (this.currentResults?.length === 0 || this.searchLaunched) && this.currentQuery?.length > 0)
    ).subscribe(() => this.resetSearchInput());
    this.form.get('input').valueChanges.pipe(
      filter((value: string) => value.length >= 5 || specials.includes(value.toLowerCase())),
      debounce(() => timer(500)),
      // distinctUntilChanged(),
      switchMap((query) => this.searchNotions(query))
    ).subscribe((result: any) => {
      if (Array.isArray(result)) {
        this.notionSuggestions = result;
      }
    });

    this.searchValue$.subscribe(val => this.currentQuery = val || '');
    this.searchResults$.subscribe(res => {
      this.currentResults = Array.isArray(res) ? res : [];
      if (!this.waitingForArticleResults) {
        this.updateCombinedResults();
      }
    });

    this.searchValue$.pipe(take(1)).subscribe(val => {
      if (val) {
        this.form.get('input').setValue(val, { emitEvent: false });
        this.articleSearchService.searchArticles(val).subscribe(res => {
          this.articleResults = res.results || [];
          this.updateCombinedResults();
        });
      }
    });
    this.angularRouter.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (event.url === '/news' || event.url.startsWith('/news?')) {
          setTimeout(() => {
            this.searchStateService.setSearchActive(this.searchLaunched || this.combinedResults.length > 0);
          }, 0);
        }
      }
    });
  }

  onInputFocus() {
    this.showSuggestionBox = true;
  }

  searchNotions(query: string) {
    return this.searchService.getNotionSuggestions(query);
  }
  
  selectSuggestion(event: MouseEvent, suggestion: string) {
    event.stopPropagation();
    this.showSuggestionBox = false;
    this.notionSuggestions = [];
    this.showArticles = false;
    this.showRemainingFaqs = false;
    this.form.get('input').setValue(suggestion, { emitEvent: false });
    this.resetContactSection();
    this.store.dispatch(search({ query: suggestion }));
  }

  waitingForArticleResults = false;

  onSubmit() {
    const query = this.form.get('input').value;
    if (query && query.length >= 3) {
      this.showSuggestionBox = true;
      this.searchLaunched = true;
      this.showArticles = false;
      this.showRemainingFaqs = false;
      this.resetContactSection();
      this.waitingForArticleResults = true;
      this.searchActiveChange.emit(true);
      this.searchStateService.setSearchLaunched(true);
      this.store.dispatch(search({ query }));
      this.articleSearchService.searchArticles(query).subscribe(res => {
        this.articleResults = res.results || [];
        this.waitingForArticleResults = false;
        this.updateCombinedResults();
        this.lastActive = true;
      });
    }
  }

  resetSearchInput() {
    this.store.dispatch(clearSearch());
    this.notionSuggestions = [];
    this.showSuggestionBox = false;
    this.form.get('input').setValue('', { emitEvent: false });
    this.articleResults = [];
    this.combinedResults = [];
    this.searchLaunched = false;
    this.showArticles = false;
    this.showRemainingFaqs = false;
    this.searchActiveChange.emit(false);
    this.searchStateService.clear();
    this.lastActive = false;
  }

  private updateCombinedResults() {
    if (this.waitingForArticleResults) return;
    this.combinedResults = [
      ...this.currentResults,
      ...this.articleResults.map(a => ({
        uuid: a.uuid,
        title: a.title,
        response: a.body,
        keywords: [],
        is_enabled: a.is_enabled,
        thumbnail: a.thumbnail,
        from: 'article' as 'faq' | 'news' | 'article',
        type: 'Article',
        category: '',
        created_at: a.created_at ? (typeof a.created_at === 'number' ? new Date(a.created_at * 1000).toISOString() : a.created_at) : undefined,
        publication_date: a.publication_date ? (typeof a.publication_date === 'number' ? new Date(a.publication_date * 1000).toISOString() : a.publication_date) : undefined
      }))
    ];
    this.searchStateService.setSearchResults(this.combinedResults);
    this.updateActiveArticleChip();
    
    const firstFaqCount = Math.min(this.getNonArticleResults().length, 6);
    const hasArticles = this.getArticleResults().length > 0;
    const hasRemainingFaqs = this.getRemainingFaqResults().length > 0;
    
    const articleDelay = firstFaqCount > 0 ? (firstFaqCount * 80) + 250 + 200 : 400;
    const remainingFaqDelay = hasArticles ? articleDelay + 600 : articleDelay;
    
    if (hasArticles) {
      setTimeout(() => {
        this.showArticles = true;
      }, articleDelay);
    } else {
      this.showArticles = false;
    }
    
    if (hasRemainingFaqs) {
      setTimeout(() => {
        this.showRemainingFaqs = true;
      }, remainingFaqDelay);
    } else {
      this.showRemainingFaqs = false;
    }
  }

  private resetContactSection() {
    this.contactRequestStatus = null;
    this.contactResponseMessage = '';
    this.contactForm.reset();
  }

  async goToResult(result: SearchResult) {
    this.searchId$.pipe(takeOperator(1)).subscribe(id => {
      if (id != null) {
        this.store.dispatch(searchClick({ searchId: id }));
      }
    });
    switch (result.from) {
      case 'news':
      case 'article':
        this.router.navigate([`article/${result.uuid}`]);
        break;
      case 'faq':
        this.router.navigate([`question/${result.uuid}`]);
        break;
      default:
        this.router.navigate([`question/${result.uuid}`]);
        break;
    }
  }

  goToArticle(article: ArticleSearchResult) {
    this.router.navigate([`article/${article.uuid}`]);
  }

  submitSearchContact() {
    if (this.contactForm.valid) {
      const query = this.form.get('input').value;
      const prefix = `Lors de la recherche du mot clé "${query}", l'utilisateur a souhaité vous contacter, voici son message à ce sujet :\n\n`;
      const text = prefix + this.contactForm.value.message;
      this.domain$.pipe(take(1)).subscribe(domain => {
        const domainMail$ = domain.contact?.email
          ? this.http.post(`${environment.apiUrl}/contact/mail-domain`, { domain: domain.label, message: text, email: domain.contact.email })
              .pipe(catchError(() => of(null)))
          : of(null);
        const notice = domain.contact?.email
          ? ''
          : `Attention, le domaine ${domain.label} n'a pas reçu ce mail car l'adresse de contact n'est pas définie\n\n`;
        const teamMail$ = this.http.post(
          `${environment.apiUrl}/contact/mail-team`,
          { domain: domain.label, message: notice + text }
        );
        forkJoin([domainMail$, teamMail$]).subscribe(
          () => {
            this.contactRequestStatus = 'success';
            this.contactResponseMessage = 'Votre message a été envoyé avec succès !';
          },
          () => {
            this.contactRequestStatus = 'error';
            this.contactResponseMessage = 'Une erreur est survenue. Veuillez réessayer ultérieurement.';
          }
        );
      });
    }
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    const inputElement = this.searchInput.nativeElement;
    if (
      event.target !== inputElement &&
      inputElement === document.activeElement
    ) {
      inputElement.blur();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event) {
    this.showSuggestionBox = false;
  }

  @HostListener('window:wheel', ['$event'])
  onWindowWheel(event: WheelEvent) {
    this.showSuggestionBox = false;
  }

  @HostListener('document:touchmove', ['$event'])
  onDocumentTouchMove(event: TouchEvent) {
    this.showSuggestionBox = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
  }

  getThumbnailUrl(article: SearchResult) {
    return `${this.assetUrl}/thumbnails/${article.thumbnail}`;
  }

  getArticleResults(): SearchResult[] {
    return this.combinedResults.filter(result => result.from === 'article');
  }

  getNonArticleResults(): SearchResult[] {
    return this.combinedResults.filter(result => result.from !== 'article');
  }

  getFirstFaqResults(): SearchResult[] {
    const nonArticleResults = this.getNonArticleResults();
    return nonArticleResults.slice(0, 6);
  }

  getRemainingFaqResults(): SearchResult[] {
    const nonArticleResults = this.getNonArticleResults();
    return nonArticleResults.slice(6);
  }

  scrollArticlesLeft() {
    if (this.articlesList?.nativeElement) {
      this.articlesList.nativeElement.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }

  scrollArticlesRight() {
    if (this.articlesList?.nativeElement) {
      this.articlesList.nativeElement.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }

  onArticlesScroll() {
    this.updateActiveArticleChip();
  }

  updateActiveArticleChip() {
    if (!this.articlesList?.nativeElement) return;
    
    const listElement = this.articlesList.nativeElement;
    const scrollLeft = listElement.scrollLeft;
    const viewportWidth = listElement.offsetWidth;

    let maxVisibleWidth = 0;
    let newActiveIndex = 0;

    const children = listElement.children;

    for (let i = 0; i < children.length; i++) {
      const card = children[i] as HTMLElement;
      const cardStart = card.offsetLeft - scrollLeft;
      const cardEnd = cardStart + card.offsetWidth;

      const visibleWidth = Math.min(cardEnd, viewportWidth) - Math.max(cardStart, 0);

      if (visibleWidth > maxVisibleWidth) {
        maxVisibleWidth = visibleWidth;
        newActiveIndex = i;
      }
    }

    this.activeArticleIndex = newActiveIndex;
  }

  activeArticleChipColor() {
    if (this.environment.application === 'tract') {
      return ' bg-[#FFA500] ';
    }
    return ' bg-yellow-400';
  }
}
